import ChevronLeft from '@mui/icons-material/ChevronLeft';
import cx from 'classnames';
import {
  FormEvent, useEffect, useRef, useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { localizeServerMessage } from '../i18n/errors';
import { useAppDispatch } from '../app/hooks';
import Alert from '../components/Alert';
import Button from '../components/Button';
import Field, { fieldLabelId } from '../components/Field';
import { Column, Container, Row } from '../components/Grid';
import { PasswordInput } from '../components/PasswordInput';
import TextInput from '../components/TextInput';
import { PageMain } from '../components/SkipLink';
import { login } from '../features/auth/authSlice';
import styles from '../sass/components/CardPage.module.scss';
import { api } from '../util/api';
import type { RootState } from '../app/store';
import { isValidEmail, useFocusFirstInvalid } from '../util/formA11y';
import usePageTitle from '../util/usePageTitle';
import { demoLoginFromParams, isDemoLoginEmail } from '../util/demoLogin';
import { normalizeThemePreset } from '../util/themePreset';
import { useTenant } from '../components/TenantContext';
import {
  ROUTE_ACCOUNT_ROOT,
  ROUTE_AUTH_FORGOT_PASSWORD,
  ROUTE_AUTH_SIGN_UP,
  ROUTE_DRIVER,
  ROUTE_FRANCHISE_MANAGER_WORK_ORDERS,
  ROUTE_ROOT,
} from '../util/constants';

export const authenticate = (
  credentials: {
    email: string,
    password: string,
  },
  onSuccess: (
    token: string,
    roles: string[],
    name: string,
    id: number,
    franchiseId: number,
    franchiseIds: number[],
    vendorId: number,
  ) => void,
  onIncorrectCredentials: (message: string) => void,
  onError: (message: string) => void,
) => {
  api.post(
    'auth/login',
    {
      // No tenant in the body: the server takes the brand from the page's
      // address (TenantHeaderMiddleware), so a Go Oil customer signing in on
      // Aperio is checked against Aperio and carried over
      // (auth/login-carry-over.ts). A fixed `tenantId: 1` used to ride here.
      email: credentials.email,
      password: credentials.password,
    },
  ).then((response) => {
    if (response.data) {
      const {
        // eslint-disable-next-line max-len
        authentication_token: token, roles, franchiseId, franchiseIds, name, id, vendorId,
      } = response.data;
      if (token) {
        onSuccess(token, roles, name, id, franchiseId, franchiseIds, vendorId);
      }
    }
  }).catch((ex) => {
    const { statusCode, message } = ex.response.data;
    if (statusCode === 400) {
      onIncorrectCredentials(
        localizeServerMessage(message, 'auth.incorrect'),
      );
    } else {
      onError(i18n.t('auth.whoopsLong'));
    }
  });
};

export default function Login() {
  const { t } = useTranslation();
  const tenant = useSelector((state: RootState) => state.tenant);
  const [params] = useSearchParams();
  // `?email=` (a "log in" link that knows the address) fills it in.
  // `?demo=fleet` / `?demo=personal` (or a demo `?email=`) signs straight
  // into an investor demo account (util/demoLogin.ts).
  const [demoLink] = useState(() => demoLoginFromParams(params));
  const [email, setEmail] = useState(
    () => demoLink || params.get('email')?.trim() || '',
  );
  const [password, setPassword] = useState('');
  const [tenantId] = useState(tenant.id);
  // Aperio: Go Oil customers sign in with their Go Oil email (the server
  // carries the login over, auth/login-carry-over.ts).
  const aperio = normalizeThemePreset(useTenant()?.themePreset) === 'aperio';
  const setPasswordLink = `${ROUTE_AUTH_FORGOT_PASSWORD}${
    isValidEmail(email) ? `?email=${encodeURIComponent(email.trim())}` : ''
  }`;
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleEmailChange = (event: FormEvent<HTMLInputElement>) => {
    setEmail(event.currentTarget.value);
  };

  const handlePasswordChange = (event: FormEvent<HTMLInputElement>) => {
    setPassword(event.currentTarget.value);
  };

  const backToBooking = () => {
    navigate(ROUTE_ROOT);
  };

  const signUp = () => {
    navigate(ROUTE_AUTH_SIGN_UP);
  };

  const setUser = async (
    token: string,
    roles: string[],
    name: string,
    id: number,
    franchiseId?: number,
    franchiseIds?: number[],
    vendorId?: number,
  ) => {
    localStorage.removeItem('hasSeenReferral');
    dispatch(login({
      email, token, roles, franchiseId, franchiseIds, name, id, vendorId,
    }));
    if (roles.includes('manager')
      || roles.includes('technician')
      || roles.includes('tester')
      || roles.includes('sales')) {
      navigate(
        ROUTE_FRANCHISE_MANAGER_WORK_ORDERS,
      );
    } else if (roles.includes('fleet_driver') && !roles.includes('fleet_manager')) {
      navigate(ROUTE_DRIVER);
    } else navigate(ROUTE_ACCOUNT_ROOT);
  };
  const handleLogin = () => {
    setError('');

    const onIncorrectCredentials = (message: string) => {
      setError(message);
    };

    const onError = () => {
      setError(t('auth.whoops'));
    };

    // A demo login needs no password; the server ignores what is sent.
    const demo = isDemoLoginEmail(email);
    authenticate(
      { email: email.trim(), password: password || (demo ? 'demo' : '') },
      setUser,
      onIncorrectCredentials,
      onError,
    );
  };

  // An empty or malformed submit says what is missing, on the field, and
  // takes focus there (QA 2026-09-25 #31); the button is never a silent
  // grey "disabled" any more.
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const focusFirstInvalid = useFocusFirstInvalid(formRef);
  let emailError: string | undefined;
  if (submitted && !email.trim()) emailError = t('auth.errors.emailRequired');
  else if (submitted && !isValidEmail(email)) {
    emailError = t('auth.errors.emailInvalid');
  }
  const demoEmail = isDemoLoginEmail(email);
  const passwordError = submitted && !password && !demoEmail
    ? t('auth.errors.passwordRequired') : undefined;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!email.trim() || !isValidEmail(email) || (!password && !demoEmail)) {
      setError('');
      focusFirstInvalid();
      return;
    }
    handleLogin();
  };

  const demoStarted = useRef(false);
  useEffect(() => {
    if (!demoLink || demoStarted.current) return;
    demoStarted.current = true;
    handleLogin();
    // Once, on arrival from a demo link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoLink]);

  usePageTitle(t('auth.login.title'));

  return (
    <PageMain className={styles.body}>
      <div className={styles.header}>
        <Button
          variant="tertiary"
          onClick={backToBooking}
        >
          <ChevronLeft />
          {t('assistance.backToBooking')}
        </Button>
      </div>
      <div className={styles.headerWrapper}>
        <h1 className={styles.desktopHeader}>{t('auth.login.title')}</h1>
        {/* The phone-sized copy of the h1: the same level, so the outline
            never starts at h3 (QA 2026-09-25 #29). */}
        <h3 className={styles.mobileHeader} aria-level={1}>
          {t('auth.login.title')}
        </h3>
        {/* A subtitle, not a section: h5 only for its look. */}
        <h5 className={styles.desktopHeader} role="presentation">
          {t('auth.login.subtitle', { name: tenant.name })}
        </h5>
        <p className={styles.mobileHeader}>
          {t('auth.login.subtitle', { name: tenant.name })}
        </p>
      </div>
      <div>
        <Container fluid>
          <Row>
            <Column md={6} mdOffset={3}>
              <div className={styles.card}>
                <Container fluid>
                  <Row>
                    <Column md={12} xs={12} lg={6}>
                      <div className={cx(
                        styles.cardColumn,
                        styles.firstCardColumn,
                      )}
                      >
                        <form
                          onSubmit={handleSubmit}
                          ref={formRef}
                          noValidate
                        >
                          <Field label={t('auth.email')} id="email">
                            <TextInput
                              id="email"
                              name="email"
                              type="email"
                              labelledBy={fieldLabelId('email')}
                              value={email}
                              onChange={handleEmailChange}
                              placeholder={t('auth.email')}
                              autoComplete="username"
                              required
                              errorMessage={emailError}
                            />
                          </Field>
                          <Field label={t('auth.password')} id="password">
                            <PasswordInput
                              id="password"
                              name="password"
                              labelledBy={fieldLabelId('password')}
                              value={password}
                              placeholder={t('auth.password')}
                              onChange={handlePasswordChange}
                              autocomplete="current-password"
                              required={!demoEmail}
                              errorMessage={passwordError}
                            />
                          </Field>
                          <Alert message={error} type="error" role="alert" />
                          {aperio && (
                            <p className="apr-login-goil">
                              {t('auth.login.goOil')}
                              {' '}
                              <Link to={setPasswordLink}>
                                {t('auth.login.setPassword')}
                              </Link>
                            </p>
                          )}
                          <Link
                            className={styles.link}
                            to={setPasswordLink}
                          >
                            {t('auth.login.forgot')}
                          </Link>
                          <Button
                            variant="primary"
                            type="submit"
                          >
                            {t('auth.login.title')}
                          </Button>
                        </form>
                      </div>
                    </Column>
                    <Column md={12} xs={12} lg={6}>
                      <div className={cx(
                        styles.centered,
                        styles.cardColumn,
                        styles.lastCardColumn,
                      )}
                      >
                        <h4 aria-level={2}>
                          {t('auth.login.noAccountTitle')}
                        </h4>
                        <p>{t('auth.login.noAccountBody', { name: tenant.name })}</p>
                        <Button
                          onClick={backToBooking}
                          className={styles.fullWidthBottomButton}
                        >
                          {t('auth.login.book')}
                        </Button>
                        <Button
                          onClick={signUp}
                          className={styles.fullWidthBottomButton}
                        >
                          {t('location.signUp')}
                        </Button>
                      </div>
                    </Column>
                  </Row>
                </Container>
              </div>
            </Column>
          </Row>
        </Container>
      </div>
    </PageMain>
  );
}
